// Policy custom-field DEFINITIONS — agency-shared field NAMES, grouped per
// policy type. Mirrors policyStatuses.js / policyTypes.js: a singleton catalog
// seeded once from agency settings at login so PolicyForm and PolicyCard can
// read it without prop-drilling through the deeply-nested Policies/ClientDetail
// trees.
//
// Only the field LABELS are shared here, keyed by the frontend short policy-type
// id (e.g. "aca" / "life" / "auto"). The per-policy VALUES stay on each policy
// as Policy.customFields ([{label,value}]) — so "Group #" is one shared field on
// every health policy, but its value is whatever each client's policy carries.
//
// Shape persisted in AgencySetting.policyCustomFieldDefs:
//   { [policyTypeId]: ["Group #", "Plan code", ...] }

let _defs = {}; // { typeId: [label, ...] }

// Normalize an incoming map: drop blanks, trim, dedupe labels case-insensitively
// within each type, and drop types that end up with no labels.
function clean(map) {
  const out = {};
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    for (const [type, labels] of Object.entries(map)) {
      if (!type || !Array.isArray(labels)) continue;
      const seen = new Set();
      const list = [];
      for (const raw of labels) {
        const label = String(raw || '').trim();
        if (!label || seen.has(label.toLowerCase())) continue;
        seen.add(label.toLowerCase());
        list.push(label);
      }
      if (list.length) out[type] = list;
    }
  }
  return out;
}

// Seed the catalog from agency settings (called once at login). Replaces it.
export function setPolicyCustomFieldDefs(map) { _defs = clean(map); }

// All defined field labels for a given policy type (e.g. "aca" → ["Group #"]).
export function getPolicyCustomFieldDefs(typeId) {
  return Array.isArray(_defs[typeId]) ? _defs[typeId].slice() : [];
}

// The full map — a deep copy, used when persisting back to agency settings.
export function getAllPolicyCustomFieldDefs() {
  return JSON.parse(JSON.stringify(_defs));
}

// Add one or more labels to a type's shared catalog (case-insensitive dedupe).
// Mutates the in-memory catalog and returns the FULL map so the caller can
// persist it via updateAgencySettings({ policyCustomFieldDefs }). Returns null
// when nothing new was added so the caller can skip the network write.
export function addPolicyCustomFieldDefs(typeId, labels) {
  if (!typeId) return null;
  // Clamp to the 60 chars the policy API enforces on customFields[].label.
  // The catalog and the per-policy payload are the same list, so a name this
  // side accepts but the API rejects makes every policy of the type unsavable
  // (Maricarmen/Pomares 2026-08-07). Clamping on the way IN also cleans a
  // catalog that was already poisoned, since this map is what gets persisted.
  const incoming = (Array.isArray(labels) ? labels : [labels])
    .map((l) => String(l || '').trim().slice(0, 60))
    .filter(Boolean);
  if (!incoming.length) return null;
  const current = Array.isArray(_defs[typeId]) ? _defs[typeId].slice() : [];
  const lower = new Set(current.map((l) => l.toLowerCase()));
  let changed = false;
  for (const label of incoming) {
    if (!lower.has(label.toLowerCase())) {
      current.push(label);
      lower.add(label.toLowerCase());
      changed = true;
    }
  }
  if (!changed) return null;
  _defs = { ..._defs, [typeId]: current };
  return getAllPolicyCustomFieldDefs();
}

// Remove a label from a type's catalog (used by the manager UI). Returns the
// full map to persist, or null when nothing changed.
export function removePolicyCustomFieldDef(typeId, label) {
  const current = Array.isArray(_defs[typeId]) ? _defs[typeId] : [];
  const target = String(label || '').trim().toLowerCase();
  const next = current.filter((l) => l.toLowerCase() !== target);
  if (next.length === current.length) return null;
  _defs = { ..._defs };
  if (next.length) _defs[typeId] = next; else delete _defs[typeId];
  return getAllPolicyCustomFieldDefs();
}

// Rename a label IN PLACE within a type's catalog (preserves order). If the new
// label already exists elsewhere in the type (case-insensitive) the old entry is
// merged away instead of creating a duplicate. Returns the full map to persist,
// or null when nothing changed (blank new label, old label not found, or no-op).
// The per-policy VALUES are migrated separately by the caller — this only
// touches the shared NAME catalog.
export function renamePolicyCustomFieldDef(typeId, oldLabel, newLabel) {
  const current = Array.isArray(_defs[typeId]) ? _defs[typeId] : [];
  const from = String(oldLabel || '').trim().toLowerCase();
  const to = String(newLabel || '').trim();
  if (!to) return null;
  const idx = current.findIndex((l) => l.toLowerCase() === from);
  if (idx < 0) return null;
  if (current[idx] === to) return null; // exact no-op
  const existsElsewhere = current.some((l, i) => i !== idx && l.toLowerCase() === to.toLowerCase());
  const next = existsElsewhere
    ? current.filter((_, i) => i !== idx)
    : current.map((l, i) => (i === idx ? to : l));
  _defs = { ..._defs, [typeId]: next };
  return getAllPolicyCustomFieldDefs();
}
